import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/auth/auth.service';
import { safeReturnUrl } from '../../core/auth/return-url';

/** The seeded development accounts (DevelopmentDataSeeder); shown only when the environment allows it. */
const TEST_PERSONAS = [
  { label: 'Initiator (Ali)', icon: 'add_circle', username: 'initiator', password: 'User@123' },
  { label: 'Dept Head (Bilal)', icon: 'supervisor_account', username: 'depthead', password: 'User@123' },
  { label: 'IT Director (Tariq)', icon: 'devices', username: 'itdir', password: 'User@123' },
  { label: 'Fin Manager (Farhan)', icon: 'payments', username: 'finmgr', password: 'User@123' },
  { label: 'CFO (Zubair)', icon: 'military_tech', username: 'cfo', password: 'User@123' },
  { label: 'Admin', icon: 'admin_panel_settings', username: 'admin', password: 'Admin@123' },
];

@Component({
  selector: 'app-login',
  imports: [FormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatIconModule, MatInputModule, RouterLink],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly personas = environment.showTestPersonas ? TEST_PERSONAS : [];
  readonly busy = signal(false);
  readonly errorMessage = signal('');
  readonly showPassword = signal(false);

  submit(usernameOrEmail: string, password: string): void {
    if (!usernameOrEmail.trim() || !password) {
      this.errorMessage.set('Enter your username or email and your password.');
      return;
    }
    this.errorMessage.set('');
    this.busy.set(true);
    this.auth.login(usernameOrEmail, password).subscribe((success) => {
      this.busy.set(false);
      if (success) void this.router.navigateByUrl(this.returnUrl());
      else this.errorMessage.set('Invalid username/email or password.');
    });
  }

  signInAs(persona: { username: string; password: string }): void {
    this.submit(persona.username, persona.password);
  }

  microsoftSignIn(): void {
    this.errorMessage.set('');
    this.auth.signInWithMicrosoft().subscribe((result) => {
      if (!result.success) {
        this.errorMessage.set(result.error ?? 'Microsoft sign-in failed.');
        return;
      }
      void this.router.navigateByUrl(this.returnUrl());
    });
  }

  private returnUrl(): string {
    return safeReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl')) ?? '/';
  }
}
